import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { showToast } from '../../utils/toastUtils';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [paginationHistory, setPaginationHistory] = useState([]);
  const [currentKey, setCurrentKey] = useState(null);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [pageSize] = useState(10);
  const [file, setFile] = useState(null);
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    useCases: '',
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
      fetchCustomers(storedUserId);
    } else {
      showToast('User ID not found. Please log in again.', 'error');
      window.location.href = process.env.REACT_APP_LOGIN_URL;
    }
  }, []);

  const fetchCustomers = async (userId, direction = 'next') => {
    try {
      let params = { pageSize, userId };
      let newHistory = [...paginationHistory];

      if (direction === 'next') {
        if (currentKey) {
          params.lastEvaluatedKey = currentKey;
          newHistory.push(currentKey);
        }
      } else if (direction === 'previous') {
        if (newHistory.length > 1) {
          newHistory.pop(); // Remove the current key
          params.lastEvaluatedKey = newHistory[newHistory.length - 1];
        } else {
          // If we're on the first page, don't do anything
          return;
        }
      }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/customers`, { params });

      setCustomers(response.data.customers || []);
      setCurrentKey(response.data.lastEvaluatedKey || null);
      setPaginationHistory(newHistory);
      setTotalCustomers(response.data.totalCustomers || 0);

      // If there's no lastEvaluatedKey, we've reached the end
      if (!response.data.lastEvaluatedKey && direction === 'next') {
        setCurrentKey(null);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      showToast('Failed to fetch customers', 'error');
    }
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) {
      toast('Please select a file to upload', {
        style: { background: 'yellow', color: 'black' }
      });
      return;
    }

    const allowedExtensions = ['csv', 'xls', 'xlsx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      toast.error('Please upload only CSV or Excel files');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);

    let simulatedProgress = 0;
    const simulateProgress = () => {
      simulatedProgress += Math.random() * 10;
      if (simulatedProgress > 90) simulatedProgress = 90;
      setUploadProgress(Math.round(simulatedProgress));
    };

    const progressInterval = setInterval(simulateProgress, 200);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/customers/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(Math.max(percentCompleted, simulatedProgress));
        },
      });
      clearInterval(progressInterval);
      setUploadProgress(100);
      setTimeout(() => {
        showToast('File uploaded successfully', 'success');
        setUploadProgress(0);
        fetchCustomers(userId);
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Error uploading file:', error.response ? error.response.data : error.message);
      showToast(`Error uploading file: ${error.response ? error.response.data.message : error.message}`, 'error');
      setUploadProgress(0);
    }
  };

  const handleAddCustomer = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/customers`,
        { ...newCustomer, userId }
      );
      setCustomers([...customers, response.data]);
      setShowAddCustomerForm(false);
      setNewCustomer({ name: '', email: '', phone: '', company: '', useCases: '' });
      toast.success('Customer added successfully');
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error('Error adding customer');
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/customers/${editingCustomer.id}`,
        { ...editingCustomer, userId }
      );
      setCustomers(customers.map(c => c.id === editingCustomer.id ? response.data : c));
      setEditingCustomer(null);
      toast.success('Customer updated successfully');
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Error updating customer');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_BASE_URL}/api/customers/${id}?userId=${userId}`
        );
        setCustomers(customers.filter(customer => customer.id !== id));
        toast.success('Customer deleted successfully');
      } catch (error) {
        console.error('Error deleting customer:', error);
        toast.error('Error deleting customer');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer({ ...newCustomer, [name]: value });
  };

  return (
    <div className="p-4">
      <Toaster position="top-right" className='mt-24' />
      <div className="mb-4">
        <h2 className="text-xl font-semibold mb-2">Customer Data Upload</h2>
        <div className="border-dashed border-2 border-gray-300 p-8 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <p className="mb-2">Drop files here or click to upload</p>
          <input
            type="file"
            onChange={handleFileChange}
            accept=".csv,.xls,.xlsx"
            className="hidden"
            id="fileInput"
          />
          <label htmlFor="fileInput" className="cursor-pointer bg-blue-500 text-white px-4 py-2 rounded">
            Select File
          </label>
          <p className="text-sm text-gray-500 mt-2">
            Supported formats: CSV, XLS, XLSX
          </p>
        </div>
        {file && (
          <div className="mt-4">
            <p>Selected file: {file.name}</p>
            <button
              onClick={handleFileUpload}
              className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
            >
              Upload File
            </button>
            {uploadProgress > 0 && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="mt-1 text-sm">Upload progress: {uploadProgress}%</p>
              </div>
            )}
          </div>
        )}
      </div>

      <button
        onClick={() => setShowAddCustomerForm(true)}
        className="bg-green-500 text-white px-4 py-2 rounded mb-4"
      >
        Add Customer Manually
      </button>

      {showAddCustomerForm && (
        <div className="mb-4 p-4 border border-gray-300 rounded">
          <h3 className="text-lg font-semibold mb-2">Add New Customer</h3>
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1">
              <input
                type="text"
                name="name"
                value={newCustomer.name}
                onChange={handleInputChange}
                placeholder="Name"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="flex-1">
              <input
                type="email"
                name="email"
                value={newCustomer.email}
                onChange={handleInputChange}
                placeholder="Email"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="flex-1">
              <input
                type="tel"
                name="phone"
                value={newCustomer.phone}
                onChange={handleInputChange}
                placeholder="Phone"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                name="company"
                value={newCustomer.company}
                onChange={handleInputChange}
                placeholder="Company"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                name="useCases"
                value={newCustomer.useCases}
                onChange={handleInputChange}
                placeholder="Use Cases"
                className="w-full p-2 border border-gray-300 rounded"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleAddCustomer}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Add Customer
            </button>
            <button
              onClick={() => setShowAddCustomerForm(false)}
              className="ml-2 bg-gray-300 text-gray-700 px-4 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-xl font-semibold mb-2">Customer List</h2>
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-2">S.No</th> 
              <th className="border border-gray-300 p-2">Name</th>
              <th className="border border-gray-300 p-2">Email</th>
              <th className="border border-gray-300 p-2">Phone</th>
              <th className="border border-gray-300 p-2">Company</th>
              <th className="border border-gray-300 p-2">Created At</th>
              <th className="border border-gray-300 p-2">Use Cases</th>
              <th className="border border-gray-300 p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length > 0 ? (
              customers.map((customer, index) => (
                <tr key={customer.id}>
                  <td className="border border-gray-300 p-2">{paginationHistory.length * pageSize + index + 1}</td>
                  <td className="border border-gray-300 p-2">{customer.name}</td>
                  <td className="border border-gray-300 p-2">{customer.email}</td>
                  <td className="border border-gray-300 p-2">{customer.phone}</td>
                  <td className="border border-gray-300 p-2">{customer.company}</td>
                  <td className="border border-gray-300 p-2">{customer.createdAt}</td>
                  <td className="border border-gray-300 p-2">{customer.useCases}</td>
                  <td className="border border-gray-300 p-2">
                    <button className="text-blue-500 mr-2" onClick={() => setEditingCustomer(customer)}>Edit</button>
                    <button className="text-red-500" onClick={() => handleDeleteCustomer(customer.id)}>Delete</button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="border border-gray-300 p-2 text-center">No customers found</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-4 flex justify-center items-center">
          <button
            onClick={() => fetchCustomers(userId, 'previous')}
            disabled={paginationHistory.length === 0}
            className="mx-1 px-3 py-1 rounded bg-blue-500 text-white disabled:bg-gray-300"
          >
            Previous
          </button>
          <span className="mx-2">
            Page {paginationHistory.length + 1}
          </span>
          <button
            onClick={() => fetchCustomers(userId, 'next')}
            disabled={!currentKey}
            className="mx-1 px-3 py-1 rounded bg-blue-500 text-white disabled:bg-gray-300"
          >
            Next
          </button>
        </div>
      </div>

      {editingCustomer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-semibold mb-4">Edit Customer</h3>
            <input
              type="text"
              value={editingCustomer.name}
              onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})}
              className="w-full p-2 mb-2 border border-gray-300 rounded"
              placeholder="Name"
            />
            <input
              type="email"
              value={editingCustomer.email}
              onChange={(e) => setEditingCustomer({...editingCustomer, email: e.target.value})}
              className="w-full p-2 mb-2 border border-gray-300 rounded"
              placeholder="Email"
            />
            <input
              type="tel"
              value={editingCustomer.phone}
              onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
              className="w-full p-2 mb-2 border border-gray-300 rounded"
              placeholder="Phone"
            />
            <input
              type="text"
              value={editingCustomer.company}
              onChange={(e) => setEditingCustomer({...editingCustomer, company: e.target.value})}
              className="w-full p-2 mb-2 border border-gray-300 rounded"
              placeholder="Company"
            />
            <input
              type="text"
              value={editingCustomer.useCases}
              onChange={(e) => setEditingCustomer({...editingCustomer, useCases: e.target.value})}
              className="w-full p-2 mb-2 border border-gray-300 rounded"
              placeholder="Use Cases"
            />
            <button onClick={handleUpdateCustomer} className="bg-blue-500 text-white px-4 py-2 rounded mr-2">
              Update
            </button>
            <button onClick={() => setEditingCustomer(null)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
